import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useState } from "react";
import Login from "../components/models/login";
import Register from "../components/models/register";
import { ToastContext } from "./ToastContext";
import axiosClient from "../utils/axiosClient";

type Props = {
	children: ReactNode;
};

export interface AuthContextTypes {
	userState?: UserState;
	isLogin: boolean;
	isLoading: boolean;
	isError: boolean;
	toast: boolean;
	message: string;
	dashboardData: any;
	setDashboardData: Dispatch<any>;
	getProfileDashboardDetails: () => void;
	handleModels: HandleModelsTypes;
	setHandleModels: Dispatch<SetStateAction<HandleModelsTypes>>;
	setUserState: Dispatch<SetStateAction<UserState>>;
	setAuthState: Dispatch<
		SetStateAction<{
			isLogin: boolean;
			isLoading: boolean;
			isError: boolean;
			toast: boolean;
			message: string;
			profileData: profileData;
			callsData: callsDataType;
			callDetails: CallDetailsType;
		}>
	>;
	profileData: profileData;
	callsData: callsDataType;
	callDetails: CallDetailsType;
}

const initState = {
	isLogin: Object.keys(JSON.parse(localStorage.getItem("user") ?? "{}")).length ? true : false,
	isLoading: false,
	isError: false,
	toast: false,
	message: "",
	profileData: {
		name: "",
		outstandingAmount: "",
		todayEarnings: "",
		todayCalls: "",
		totalSubscribers: "",
		todaySubscribers: "",
		rating: "",
		active: "",
		whatsappNumber: "",
		callerId: "",
		profileImage: "",
	},
	callsData: {
		mycalls: [{
			amount: "",
			callExpireTime: "",
			callExpiredTime: "",
			callerCompanyName: "",
			callerId: "",
			callsUpdatedAt: "",
			callstatus: "",
			createdAt: "",
			hostName: "",
			id: "",
			stockExchange: "",
			stopLoss: "",
			target1: "",
			target2: "",
			target3: "",
			tradeCompany: "",
			tradeDetails: "",
			tradeType: "",
			updateCount: "",
			updatedAt: "",
			viewed: "",
		},
		],
		totalPages: 0,
	},
	callDetails: {
		amount: "",
		callExpireTime: "",
		callExpiredTime: "",
		callerCompanyName: "",
		callerId: "",
		callsUpdatedAt: "",
		callstatus: "",
		createdAt: "",
		hostName: "",
		id: "",
		stockExchange: "",
		stopLoss: "",
		target1: "",
		target2: "",
		target3: "",
		tradeCompany: "",
		tradeDetails: "",
		tradeType: "",
		updateCount: "",
		updatedAt: "",
		viewed: "",
		tradeSymbol: "",
		putCall: "",
		buySell: "",
	},
};

export type HandleModelsTypes = {
	for: null | "register" | "login";
	state: {
		countryCode: string;
		mobile: string;
		otp: string;
		profile?: string;
		businessName?: string;
		sebiNumber?: string;
		tradeType?: string;
	};
};

export const initHandleModels: HandleModelsTypes = {
	for: null,
	state: { countryCode: "+91", mobile: "", otp: "" },
};

export type Caller = {
	aadharUrl: string;
	active: boolean;
	blocked: boolean;
	businessName: string;
	createdAt: string;
	description: string;
	id: number;
	profileImage: string;
	rating: number;
	sebiNumber: string;
	subscriptionId: string;
	telephone: string;
	tradeType: Array<string>;
	updatedAt: string;
	verified: boolean;
};

export type User = {
	blocked: boolean;
	createdAt: string;
	deviceToken: string;
	devices: string;
	id: number;
	name: string;
	otp: string;
	rechargeButton: boolean;
	referralCode: string;
	subscribeButton: boolean;
	telephone: string;
	updatedAt: string;
	upi: string;
	verified: boolean;
};

export type UserState = {
	user?: User;
	caller?: Caller;
	otpVerified: boolean;
	sessionToken: string;
};

export type profileData = {
	name: string;
	outstandingAmount: string;
	todayEarnings: string;
	todayCalls: string;
	totalSubscribers: string;
	todaySubscribers: string;
	rating: string;
	active: string;
	whatsappNumber: string;
	callerId: string;
	profileImage: string;
};

export type callsDataType = {
	mycalls: {
		amount: string;
		callExpireTime: string;
		callExpiredTime: string;
		callerCompanyName: string;
		callerId: string;
		callsUpdatedAt: string;
		callstatus: string;
		createdAt: string;
		hostName: string;
		id: string;
		stockExchange: string;
		stopLoss: string;
		target1: string;
		target2: string;
		target3: string;
		tradeCompany: string;
		tradeDetails: string;
		tradeType: string;
		updateCount: string;
		updatedAt: string;
		viewed: string;
	}[];
	totalPages: number;
};

export type CallDetailsType = {
	amount: string;
	callExpireTime: string;
	callExpiredTime: string;
	callerCompanyName: string;
	callerId: string;
	callsUpdatedAt: string;
	callstatus: string;
	createdAt: string;
	hostName: string;
	id: string;
	stockExchange: string;
	stopLoss: string;
	target1: string;
	target2: string;
	target3: string;
	tradeCompany: string;
	tradeDetails: string;
	tradeType: string;
	updateCount: string;
	updatedAt: string;
	viewed: string;
	tradeSymbol: string;
	putCall: string;
	buySell: string;
};

export const AuthContext = createContext<AuthContextTypes>({
	handleModels: initHandleModels,
	setHandleModels: () => { },
	setUserState: () => { },
	...initState,
	dashboardData: null,
	getProfileDashboardDetails: () => { },
	setDashboardData: () => { },
	setAuthState: () => { },
});

export const AuthProvider: React.FC<Props> = ({ children }) => {
	const [userState, setUserState] = useState<UserState>(JSON.parse(localStorage.getItem("user") ?? "{}"));
	const [dashboardData, setDashboardData] = useState<any>(null);
	const { notify } = useContext(ToastContext);
	const [{ isLogin, isLoading, isError, toast, message, profileData, callsData, callDetails }, setAuthState] =
		useState<{
			isLogin: boolean;
			isLoading: boolean;
			isError: boolean;
			sessionToken?: string;
			toast: boolean;
			message: string;
			profileData: profileData;
			callsData: callsDataType;
			callDetails: CallDetailsType;
		}>(initState);

	const [handleModels, setHandleModels] = useState<HandleModelsTypes>(initHandleModels);

	const handleClose = () => {
		setHandleModels(initHandleModels);
	};

	useEffect(() => {
		if (toast)
			notify(message ?? "Something is wrong", {
				type: isError ? "error" : "success",
				onClose: () => {
					setAuthState((state) => ({ ...state, isError: false, toast: false, message: "" }));
				},
			});
	}, [isLoading, isError, message, notify, toast]);

	useEffect(() => {
		if (isLogin && dashboardData && !dashboardData?.active) setHandleModels({ ...handleModels, for: "register" });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLogin, dashboardData]);

	const getProfileDashboardDetails = async () => {
		try {
			const { data } = await axiosClient.get("/caller/dashboard");
			setDashboardData(data?.result);
			setAuthState((authState) => ({
				...authState,
				isLoading: false,
				profileData: data?.result,
			}));
		} catch (error: any) {
			console.log(error);
		}
	};

	useEffect(() => {
		if (userState?.sessionToken) {
			getProfileDashboardDetails();
		}
	}, [isLogin, userState]);

	return (
		<AuthContext.Provider
			value={{
				toast,
				isError,
				isLogin,
				message,
				userState,
				isLoading,
				callsData,
				profileData,
				callDetails,
				handleModels,
				dashboardData,
				setUserState,
				setAuthState,
				setHandleModels,
				setDashboardData,
				getProfileDashboardDetails,
			}}
		>
			{children}
			<Register open={handleModels.for === "register"} isLogin={isLogin} onClose={() => handleClose()} />
			<Login open={handleModels.for === "login"} onClose={() => handleClose()} />
		</AuthContext.Provider>
	);
};
